import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";

// worker ของ pdf.js ถูกก็อปไปไว้ที่ public/ ตอน build (scripts/copy-pdf-worker.js)
// ห้ามให้ webpack แพ็กเอง: babel ของ CRA จะแปลงไฟล์แล้วใส่ import แบบ path
// ในเครื่อง build (/app/node_modules/@babel/runtime/...) ทำให้ browser โหลดไม่ได้
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

const ZOOM_STEPS = [0.6, 0.8, 1, 1.25, 1.5, 2];

/**
 * Viewer สำหรับหน้าเอกสารเผยแพร่ — ให้อ่านอย่างเดียว
 *
 * วาด PDF ลง canvas เองแทนการเปิดด้วย viewer ของ browser จะได้ไม่มีปุ่ม
 * download / print และไม่มี URL ไฟล์ให้ก็อป พร้อมปิดคลิกขวา, Ctrl+S/P,
 * การเลือกข้อความ และซ่อนทั้งหน้าเวลาสั่งพิมพ์ (ดู pdf-viewer.css)
 *
 * ข้อจำกัด: กันการแคปหน้าจอไม่ได้ (เว็บไม่มี API แบบ DRM ของวิดีโอ)
 */
export default function PdfViewer({ url, title, lang, onClose }) {
  const [pdf, setPdf] = useState(null);
  const [error, setError] = useState(false);
  const [zoomIndex, setZoomIndex] = useState(2);
  const [containerWidth, setContainerWidth] = useState(0);
  const scrollRef = useRef(null);

  const t = (th, en) => (lang === "en" ? en : th);

  // โหลดเอกสาร
  useEffect(() => {
    let cancelled = false;
    let task = null;
    setPdf(null);
    setError(false);

    try {
      task = pdfjsLib.getDocument({ url, withCredentials: false });
      task.promise
        .then((doc) => {
          if (!cancelled) setPdf(doc);
        })
        .catch((e) => {
          console.log(e);
          if (!cancelled) setError(true);
        });
    } catch (e) {
      console.log(e);
      setError(true);
    }

    return () => {
      cancelled = true;
      if (task) task.destroy();
    };
  }, [url]);

  // ล็อกหน้าเว็บด้านหลัง + ปิดทางลัดบันทึก/พิมพ์ + ปิดคลิกขวา ตลอดที่ viewer เปิด
  useEffect(() => {
    document.body.classList.add("pdf-viewer-open");

    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (e.key === "Escape") onClose();
      if (mod && ["s", "p"].includes(e.key.toLowerCase())) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const block = (e) => e.preventDefault();

    window.addEventListener("keydown", onKey, true);
    window.addEventListener("contextmenu", block, true);
    window.addEventListener("beforeprint", block);

    return () => {
      document.body.classList.remove("pdf-viewer-open");
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("contextmenu", block, true);
      window.removeEventListener("beforeprint", block);
    };
  }, [onClose]);

  // ความกว้างพื้นที่อ่าน ใช้คำนวณให้หน้ากระดาษพอดีจอ
  useEffect(() => {
    const measure = () => {
      if (scrollRef.current) setContainerWidth(scrollRef.current.clientWidth);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const zoom = ZOOM_STEPS[zoomIndex];
  const pageCount = pdf ? pdf.numPages : 0;

  return (
    <div
      id="pdf-viewer"
      className="fixed inset-0 z-[300] bg-[#1E1E1E] flex flex-col select-none"
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      {/* แถบหัว */}
      <div className="flex items-center gap-[12px] px-[16px] py-[10px] bg-slateGray text-white shrink-0">
        <p className="flex-1 text-body font-medium text-left truncate">{title}</p>

        {pdf && (
          <p className="hidden sm:block text-small text-white/70 whitespace-nowrap">
            {pageCount} {t("หน้า", "pages")}
          </p>
        )}

        <div className="flex items-center gap-[4px]">
          <button
            type="button"
            aria-label={t("ย่อ", "Zoom out")}
            disabled={zoomIndex === 0}
            onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}
            className="w-[32px] h-[32px] rounded-[6px] bg-white/10 hover:bg-white/20 disabled:opacity-30 text-lead leading-none"
          >
            −
          </button>
          <span className="w-[48px] text-center text-small">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            aria-label={t("ขยาย", "Zoom in")}
            disabled={zoomIndex === ZOOM_STEPS.length - 1}
            onClick={() => setZoomIndex((i) => Math.min(ZOOM_STEPS.length - 1, i + 1))}
            className="w-[32px] h-[32px] rounded-[6px] bg-white/10 hover:bg-white/20 disabled:opacity-30 text-lead leading-none"
          >
            +
          </button>
        </div>

        <button
          type="button"
          aria-label={t("ปิด", "Close")}
          onClick={onClose}
          className="ml-[8px] w-[32px] h-[32px] rounded-[6px] bg-white/10 hover:bg-white/20 text-lead leading-none"
        >
          ×
        </button>
      </div>

      {/* พื้นที่อ่าน */}
      <div ref={scrollRef} className="relative flex-1 overflow-auto">
        {error && (
          <p className="text-white text-body text-center mt-[80px] px-[16px]">
            {t("เปิดเอกสารไม่ได้ กรุณาลองใหม่อีกครั้ง", "Could not open this document. Please try again.")}
          </p>
        )}

        {!pdf && !error && (
          <p className="text-white/70 text-body text-center mt-[80px]">
            {t("กำลังโหลดเอกสาร...", "Loading document...")}
          </p>
        )}

        {pdf && containerWidth > 0 && (
          <div className="flex flex-col items-center gap-[16px] py-[16px] px-[8px]">
            {Array.from({ length: pageCount }, (_, i) => (
              <PdfPage
                key={i}
                pdf={pdf}
                pageNumber={i + 1}
                // ความกว้างหน้ากระดาษ = พื้นที่ (หัก padding) × zoom แต่ไม่เกิน 1000px ที่ 100%
                targetWidth={Math.min(containerWidth - 16, 1000) * zoom}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** วาด 1 หน้าลง canvas ที่ความกว้างที่กำหนด คมบนจอ retina ด้วย devicePixelRatio */
function PdfPage({ pdf, pageNumber, targetWidth }) {
  const canvasRef = useRef(null);
  const [height, setHeight] = useState(Math.round(targetWidth * 1.414));

  useEffect(() => {
    let cancelled = false;
    let renderTask = null;

    pdf
      .getPage(pageNumber)
      .then((page) => {
        if (cancelled) return;
        const base = page.getViewport({ scale: 1 });
        const scale = targetWidth / base.width;
        const viewport = page.getViewport({ scale });
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        setHeight(Math.floor(viewport.height));

        const ctx = canvas.getContext("2d");
        renderTask = page.render({
          canvasContext: ctx,
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null,
        });
        return renderTask.promise;
      })
      .catch((e) => {
        // ยกเลิกเพราะ zoom/resize ระหว่างวาด ไม่ใช่ error จริง
        if (e && e.name !== "RenderingCancelledException") console.log(e);
      });

    return () => {
      cancelled = true;
      if (renderTask) renderTask.cancel();
    };
  }, [pdf, pageNumber, targetWidth]);

  return (
    <canvas
      ref={canvasRef}
      className="bg-white shadow-lg"
      style={{ width: `${Math.floor(targetWidth)}px`, height: `${height}px` }}
      draggable={false}
    />
  );
}
